/* eslint-disable no-continue */
import { differenceInMinutes } from 'date-fns';

import { HabitatListItem } from '@/queries/habitats/types';

type Habitat = Pick<
  HabitatListItem,
  '_id' | 'title' | 'isStreamOn' | 'liveTalk' | 'index' | 'currentViewers' | 'lastMediaCapture' | 'lastAnimalAlertFlaggedAt'
>;

export type HabitatSortType<T extends Habitat = Habitat> =
  | 'lastMediaCapture'
  | 'lastAnimalAlert'
  | 'liveTalk'
  | 'online'
  | 'index'
  | 'currentViewers'
  | 'title'
  | 'id'
  | ((a: T, b: T) => number);

export const defaultSorterTypes = Object.freeze([
  'liveTalk',
  'online',
  'index',
  'currentViewers',
  'title',
] as HabitatSortType[]);

/** The sort keys that rank by a recent signal, and the timestamp each one reads. */
const RECENCY_FIELDS = {
  lastAnimalAlert: 'lastAnimalAlertFlaggedAt',
  lastMediaCapture: 'lastMediaCapture',
} as const;

export const sortHabitats = <T extends Habitat>(
  habitats: T[],
  sorterTypes: readonly HabitatSortType<T>[] = defaultSorterTypes,
  recencyThresholdMinutes: number,
) => {
  const now = Date.now();

  const isRecent = (timestamp: number) => differenceInMinutes(now, timestamp) <= recencyThresholdMinutes;

  // Only a signal inside the recency threshold counts; a stale one sorts as if absent.
  const getRecentTimestampValue = (timestamp: string | undefined) => {
    const val = timestamp ? Date.parse(timestamp) : 0;
    if (!val || !isRecent(val)) {
      return 0;
    }
    return val;
  };

  return [...habitats].sort((a, b) => {
    for (let i = 0; i < sorterTypes.length; i += 1) {
      const sorterType = sorterTypes[i];
      if (sorterType instanceof Function) {
        const sorterResult = sorterType(a, b);
        if (sorterResult === 0) {
          continue;
        }
        return sorterResult;
      }

      switch (sorterType) {
        case 'lastAnimalAlert':
        case 'lastMediaCapture': {
          const field = RECENCY_FIELDS[sorterType];
          const aValue = getRecentTimestampValue(a[field]);
          const bValue = getRecentTimestampValue(b[field]);

          if (aValue !== bValue) {
            return bValue - aValue;
          }
          continue;
        }

        case 'liveTalk':
          if (a.liveTalk !== b.liveTalk) {
            return b.liveTalk ? 1 : -1;
          }
          continue;

        case 'online':
          if (a.isStreamOn !== b.isStreamOn) {
            return b.isStreamOn ? 1 : -1;
          }
          continue;

        case 'index':
          if ((a.index || Number.MAX_SAFE_INTEGER) !== (b.index || Number.MAX_SAFE_INTEGER)) {
            return (a.index || Number.MAX_SAFE_INTEGER) - (b.index || Number.MAX_SAFE_INTEGER);
          }
          continue;

        case 'currentViewers':
          if (a.currentViewers !== b.currentViewers) {
            return b.currentViewers - a.currentViewers;
          }
          continue;

        case 'id':
          // `id` should not be equal, so we can safely return the result
          return a._id.localeCompare(b._id);

        case 'title':
        default:
          // `title` should not be equal, so we can safely return the result
          return a.title.localeCompare(b.title);
      }
    }
    // No sorting if no sorter types are left
    return 0;
  });
};
